import {
  ArrowLeftOutlined,
  BoxPlotOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyOutlined,
  ShopOutlined,
  ToolOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Select,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QCStatus } from "../../enum/common";
import { useEvaluateQCResult, useGetQcResultDetail } from "../../query/QcQuery";
import { notify } from "../../utils/toast";

const { Title, Text } = Typography;
const { TextArea } = Input;

const QualityControlResultDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNumber = Number(id);
  const { data, isLoading, refetch } = useGetQcResultDetail(idNumber);
  const evaluateQCResult = useEvaluateQCResult();
  const [form] = Form.useForm();
  const [openReEvaluate, setOpenReEvaluate] = useState<boolean>(false);

  const [qtyNG, setQtyNG] = useState<number | null>(null);

  const getResultTag = (result: string) => {
    switch (result) {
      case QCStatus.PASSED:
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            color="success"
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            OK
          </Tag>
        );
      case QCStatus.REJECT:
        return (
          <Tag
            icon={<CloseCircleOutlined />}
            color="error"
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            NG
          </Tag>
        );
      case QCStatus.PARTIAL_REJECT:
        return (
          <Tag
            icon={<InfoCircleOutlined />}
            color="warning"
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            PARTIAL
          </Tag>
        );
      default:
        return <Tag>{result}</Tag>;
    }
  };

  const getSupplierTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      MANUFACTURER: "Chính hãng",
      DISTRIBUTOR: "Đại lý",
      TRADER: "Thương mại",
      OEM: "OEM",
      INTERNAL: "Nội bộ",
      OTHER: "Khác",
    };
    return map[type] || type;
  };

  const getMaterialsTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      STANDARD_PART: "Linh kiện ĐTTC",
      MECHANICS_PART: "Linh kiện cơ khí",
      PCB_A: "PCB-A",
      ELECTRONICS_PART: "Linh kiện Điện",
      MACHINES: "Máy móc thiết bị",
      OTHER: "Loại khác",
    };
    return map[type] || type;
  };

  const getPackingLabel = (type: string) => {
    const map: Record<string, string> = {
      BULK: "Bulk",
      REEL: "Reel (Cuộn)",
      TRAY: "Tray (Khay)",
      TUBE: "Tube (Ống)",
      BAG: "Bag (Túi)",
    };
    return map[type] || type;
  };

  const getDefectLabel = (type: string) => {
    const map: Record<string, string> = {
      FUNCTION: "Chức năng",
      DIMENSION: "Kích thước",
      APPEARANCE: "Ngoại quan",
      PACKING: "Đóng gói",
      CONTRACT: "Hợp đồng",
    };
    return map[type] || type;
  };

  const handleReEvaluate = () => {
    if (!data) return;
    form.setFieldsValue({
      samplingSize: data.samplingSize,
      qtyNG: data.qtyNG,
      result: data.result,
      typeError: data.typeError,
      defect: data.defect,
      detailDescription: data.detailDescription,
      action: data.action,
      follow: data.follow,
      note: data.note,
    });
    setOpenReEvaluate(true);
  };

  const handleSubmitReEvaluate = async () => {
    try {
      const values = await form.validateFields();
      await evaluateQCResult.mutateAsync({
        id: idNumber,
        data: values,
      });
      notify.success("QC lại thành công");
      setOpenReEvaluate(false);
      refetch();
    } catch (error: any) {
      notify.error(error?.response?.data?.message);
    }
  };

  const handleQtyNGChange = (value: number | null) => {
    setQtyNG(value);
    const samplingSize = form.getFieldValue("samplingSize");
    if (value !== null && samplingSize) {
      const ngRate = ((value / samplingSize) * 100).toFixed(2);
      form.setFieldsValue({ ngRate: parseFloat(ngRate) });
      calculateResult(parseFloat(ngRate));
    }
  };

  const handleSamplingSizeChange = (value: number | null) => {
    const qtyNG = form.getFieldValue("qtyNG");
    if (value !== null && qtyNG !== null) {
      const ngRate = ((qtyNG / value) * 100).toFixed(2);
      form.setFieldsValue({ ngRate: parseFloat(ngRate) });
      calculateResult(parseFloat(ngRate));
    }
  };

  const calculateResult = (ngRate: number) => {
    let result;
    let follow;
    if (ngRate === 0) {
      result = QCStatus.PASSED;
      follow = "OK";
    } else if (ngRate === 100) {
      result = QCStatus.REJECT;
      follow = "NG";
    } else {
      result = QCStatus.PARTIAL_REJECT;
      follow = "NG";
    }
    form.setFieldsValue({ result });
    form.setFieldsValue({ follow });
  };

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <Result
        status="404"
        title="404"
        subTitle="Không tìm thấy kết quả QC"
        extra={
          <Button type="primary" onClick={() => navigate(-1)}>
            Quay lại
          </Button>
        }
      />
    );
  }

  const isPassed = data.result === QCStatus.PASSED;

  return (
    <div className="p-4" style={{ minHeight: "100vh" }}>
      <div className="mb-4 flex items-center justify-between">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          size="large"
        >
          Quay lại
        </Button>
        <div className="flex items-center gap-2">
          {getResultTag(data.result)}
          {data.isReturn ? (
            <Tag
              icon={<ReloadOutlined />}
              style={{ fontSize: 14, padding: "4px 12px" }}
              color="green"
            >
              Đã trả hàng
            </Tag>
          ) : (
            <Button
              icon={<ReloadOutlined />}
              style={{ fontSize: 14, padding: "4px 12px" }}
              color="volcano"
              onClick={() => handleReEvaluate()}
            >
              Re-Quality Check
            </Button>
          )}
        </div>
      </div>

      <Card
        style={{
          marginBottom: 16,
          borderLeft: `4px solid ${isPassed ? "#52c41a" : "#ff4d4f"}`,
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
              <ExperimentOutlined className="mr-2" />
              Kết quả QC #{data.id}
            </Title>
            <Text type="secondary">
              Part Number: <Text strong>{data.partNumber.partNumber}</Text> | Dự
              án: <Text strong>{data.partNumber.project}</Text>
            </Text>
          </div>
        </div>
      </Card>

      <Row gutter={16} className="mb-4">
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="SL thực tế"
              value={data.partNumber.actualQty}
              prefix={<BoxPlotOutlined style={{ color: "#13c2c2" }} />}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="Số lượng mẫu"
              value={data.samplingSize}
              prefix={<BoxPlotOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="Số lượng NG"
              value={data.qtyNG}
              prefix={
                <CloseCircleOutlined
                  style={{ color: data.qtyNG > 0 ? "#ff4d4f" : "#52c41a" }}
                />
              }
              valueStyle={{ color: data.qtyNG > 0 ? "#ff4d4f" : "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="Tỷ lệ NG"
              value={data.ngRate}
              suffix="%"
              prefix={
                <WarningOutlined
                  style={{ color: data.ngRate > 0 ? "#faad14" : "#52c41a" }}
                />
              }
              valueStyle={{ color: data.ngRate > 0 ? "#faad14" : "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic
              title="Kết quả"
              value={isPassed ? "ĐẠT" : "KHÔNG ĐẠT"}
              prefix={
                isPassed ? (
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                ) : (
                  <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                )
              }
              valueStyle={{
                color: isPassed ? "#52c41a" : "#ff4d4f",
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <InfoCircleOutlined className="mr-2" />
                Thông tin Part Number
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item
                label={
                  <span>
                    <BoxPlotOutlined
                      className="mr-2"
                      style={{ color: "#1890ff" }}
                    />
                    Part Number
                  </span>
                }
              >
                <Text strong style={{ color: "#1890ff" }}>
                  {data.partNumber.partNumber}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <FileTextOutlined
                      className="mr-2"
                      style={{ color: "#52c41a" }}
                    />
                    Dự án
                  </span>
                }
              >
                <Text strong>{data.partNumber.project}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <FileTextOutlined
                      className="mr-2"
                      style={{ color: "#faad14" }}
                    />
                    Mã dự án
                  </span>
                }
              >
                {data.partNumber.projectCode}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <FileTextOutlined
                      className="mr-2"
                      style={{ color: "red" }}
                    />
                    AWB
                  </span>
                }
              >
                {data.partNumber.awb}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <ToolOutlined
                      className="mr-2"
                      style={{ color: "#722ed1" }}
                    />
                    Nhà sản xuất
                  </span>
                }
              >
                {data.partNumber.manufacturer}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <EnvironmentOutlined
                      className="mr-2"
                      style={{ color: "#13c2c2" }}
                    />
                    Kho gốc
                  </span>
                }
              >
                <Text strong>
                  {data.partNumber.location?.warehouse?.warehouseCode}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <EnvironmentOutlined
                      className="mr-2"
                      style={{ color: "#eb2f96" }}
                    />
                    Vị trí gốc
                  </span>
                }
              >
                <Text strong>{data.partNumber.location?.code}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <span>
                <ShopOutlined className="mr-2" />
                Thông tin nhà cung cấp
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item
                label={
                  <span>
                    <ShopOutlined
                      className="mr-2"
                      style={{ color: "#52c41a" }}
                    />
                    Tên nhà cung cấp
                  </span>
                }
              >
                <Text strong>{data.supplierName}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <SafetyOutlined
                      className="mr-2"
                      style={{ color: "#1890ff" }}
                    />
                    Loại nhà cung cấp
                  </span>
                }
              >
                {getSupplierTypeLabel(data.partNumber.supplierType)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <BoxPlotOutlined
                      className="mr-2"
                      style={{ color: "#722ed1" }}
                    />
                    Loại vật liệu
                  </span>
                }
              >
                {getMaterialsTypeLabel(data.materialsType)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <UserOutlined
                      className="mr-2"
                      style={{ color: "#fa8c16" }}
                    />
                    Khách hàng
                  </span>
                }
              >
                {data.customerName}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <span>
                <ExperimentOutlined className="mr-2" />
                Thông tin kiểm tra
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions
              column={{ xs: 1, sm: 2, md: 3 }}
              size="small"
              bordered
            >
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined
                      className="mr-2"
                      style={{ color: "#13c2c2" }}
                    />
                    Ngày hàng về
                  </span>
                }
              >
                {data.partNumber.importDate
                  ? new Date(data.partNumber.importDate).toLocaleDateString()
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined
                      className="mr-2"
                      style={{ color: "#1890ff" }}
                    />
                    Ngày kiểm tra
                  </span>
                }
              >
                {data.qcDate
                  ? new Date(data.qcDate).toLocaleDateString()
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined
                      className="mr-2"
                      style={{ color: "#722ed1" }}
                    />
                    Week
                  </span>
                }
              >
                {data.week || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined
                      className="mr-2"
                      style={{ color: "#eb2f96" }}
                    />
                    Month
                  </span>
                }
              >
                {data.month || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined
                      className="mr-2"
                      style={{ color: "#1890ff" }}
                    />
                    Date Code
                  </span>
                }
              >
                {data.dateCode}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <BoxPlotOutlined
                      className="mr-2"
                      style={{ color: "#52c41a" }}
                    />
                    Đóng gói
                  </span>
                }
              >
                {getPackingLabel(data.packing)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <EnvironmentOutlined
                      className="mr-2"
                      style={{ color: "#fa8c16" }}
                    />
                    Xuất xứ
                  </span>
                }
              >
                {data.origin}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <SafetyOutlined
                      className="mr-2"
                      style={{ color: "#722ed1" }}
                    />
                    MSL
                  </span>
                }
              >
                {data.msl}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <ExperimentOutlined
                      className="mr-2"
                      style={{ color: "#faad14" }}
                    />
                    Tỷ lệ kiểm tra
                  </span>
                }
              >
                {data.inspectionRate ? `${data.inspectionRate}%` : "N/A"}
              </Descriptions.Item>
              {data.typeError && (
                <Descriptions.Item
                  label={
                    <span>
                      <WarningOutlined
                        className="mr-2"
                        style={{ color: "#ff4d4f" }}
                      />
                      Kiểu lỗi
                    </span>
                  }
                >
                  {data.typeError === "YES"
                    ? "Lỗi do nhà cung cấp"
                    : "Lỗi nội bộ"}
                </Descriptions.Item>
              )}
              <Descriptions.Item
                label={
                  <span>
                    <UserOutlined
                      className="mr-2"
                      style={{ color: "#1890ff" }}
                    />
                    Người mua
                  </span>
                }
              >
                {data.partNumber.purchaser}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <span>
                <FileTextOutlined className="mr-2" />
                Mô tả chi tiết
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <div
              style={{
                padding: 12,
                border: "1px solid #d9d9d9",
                borderRadius: 4,
              }}
            >
              <Text>{data.detailDescription}</Text>
            </div>
          </Card>
        </Col>

        {!isPassed && (
          <Col xs={24}>
            <Card
              title={
                <span style={{ color: "#ff4d4f" }}>
                  <CloseCircleOutlined className="mr-2" />
                  Thông tin xử lý lỗi
                </span>
              }
              style={{ marginBottom: 16, borderLeft: "4px solid #ff4d4f" }}
            >
              <Descriptions
                column={{ xs: 1, sm: 2, md: 3 }}
                size="small"
                bordered
              >
                {data.defect && (
                  <Descriptions.Item
                    label={
                      <span>
                        <WarningOutlined
                          className="mr-2"
                          style={{ color: "#ff4d4f" }}
                        />
                        Loại lỗi
                      </span>
                    }
                  >
                    <Tag color="red">{getDefectLabel(data.defect)}</Tag>
                  </Descriptions.Item>
                )}
                {data.action && (
                  <Descriptions.Item
                    label={
                      <span>
                        <ToolOutlined
                          className="mr-2"
                          style={{ color: "#fa8c16" }}
                        />
                        Hành động xử lý
                      </span>
                    }
                  >
                    {data.action}
                  </Descriptions.Item>
                )}
                {data.follow && (
                  <Descriptions.Item
                    label={
                      <span>
                        <UserOutlined
                          className="mr-2"
                          style={{ color: "#1890ff" }}
                        />
                        Theo dõi
                      </span>
                    }
                  >
                    {data.follow}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>
        )}

        {data.note && (
          <Col xs={24}>
            <Card
              title={
                <span>
                  <InfoCircleOutlined className="mr-2" />
                  Ghi chú
                </span>
              }
            >
              <div
                style={{
                  padding: 12,
                  border: "1px solid #ffe58f",
                  borderRadius: 4,
                }}
              >
                <Text>{data.note}</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      <Modal
        title={
          <span>
            <ReloadOutlined className="mr-2" />
            QC lại - Đánh giá lại kết quả
          </span>
        }
        open={openReEvaluate}
        onCancel={() => setOpenReEvaluate(false)}
        onOk={handleSubmitReEvaluate}
        width={800}
        confirmLoading={evaluateQCResult.isPending}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Số lượng mẫu"
                name="samplingSize"
                rules={[
                  { required: true, message: "Vui lòng nhập số lượng mẫu" },
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  onChange={handleSamplingSizeChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Số lượng NG"
                name="qtyNG"
                rules={[
                  { required: true, message: "Vui lòng nhập số lượng NG" },
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  onChange={handleQtyNGChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Tỷ lệ NG (%)" name="ngRate">
                <InputNumber disabled style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Kết quả"
                name="result"
                rules={[{ required: true, message: "Vui lòng chọn kết quả" }]}
              >
                <Select disabled placeholder="Kết quả sẽ được tính tự động">
                  <Select.Option value={QCStatus.PASSED}>OK</Select.Option>
                  <Select.Option value={QCStatus.REJECT}>NG</Select.Option>
                  <Select.Option value={QCStatus.PARTIAL_REJECT}>
                    PARTIAL
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Mô tả chi tiết"
            name="detailDescription"
            rules={[
              { required: true, message: "Vui lòng nhập mô tả chi tiết" },
            ]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.result !== currentValues.result
            }
          >
            {({ getFieldValue }) => {
              const result = getFieldValue("result");
              if (result === QCStatus.PASSED) return null;

              return (
                <>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="Loại lỗi" name="defect">
                        <Select allowClear>
                          <Select.Option value="FUNCTION">
                            Chức năng
                          </Select.Option>
                          <Select.Option value="DIMENSION">
                            Kích thước
                          </Select.Option>
                          <Select.Option value="APPEARANCE">
                            Ngoại quan
                          </Select.Option>
                          <Select.Option value="PACKING">
                            Đóng gói
                          </Select.Option>
                          <Select.Option value="CONTRACT">
                            Hợp đồng
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Kiểu lỗi" name="typeError">
                        <Select allowClear>
                          <Select.Option value="YES">
                            Lỗi do nhà cung cấp
                          </Select.Option>
                          <Select.Option value="NO">Lỗi nội bộ</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Theo dõi" name="follow">
                        <Select
                          placeholder="Theo dõi"
                          allowClear
                          disabled={qtyNG === 0}
                        >
                          <Select.Option value="NG">NG</Select.Option>
                          <Select.Option value="OK">OK</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Hành động xử lý" name="action">
                    <TextArea rows={2} />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item label="Ghi chú" name="note">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QualityControlResultDetail;
